import { computed, Injectable, signal, Signal, WritableSignal, inject } from "@angular/core";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { FeatureFlagViewModel } from "@tylertech/tyler-api-types";
import { ApiService } from "./apiService";

export const FeatureFlags = {
    ResolveAllFeatureFlagsTo: "ResolveAllFeatureFlagsTo",
    All_DevFlag: "All_DevFlag",
    Shared_All_SmartPulseAssistant: "Shared_All_SmartPulseAssistant",
    Shared_All_SmartPulseFollowMe: "Shared_All_SmartPulseFollowMe",
    Shared_All_BrowserNotifications: "Shared_All_BrowserNotifications"
} as const;

@Injectable()
export class FeatureFlagHelperService
{
    private readonly $apiService = inject(ApiService);

    private static instance?: FeatureFlagHelperService;
    private readonly ready: Promise<void>;
    private readonly log = getLoggerFor("FeatureFlagHelperService");
    protected initPromise?: Promise<void>;
    protected readonly featureFlags = new Map<string, WritableSignal<boolean>>();

    constructor()
    {
        FeatureFlagHelperService.instance = this;
        this.initPromise = this.GetFlags()
            .then((featureFlags: Array<FeatureFlagViewModel>) =>
            {
                featureFlags.forEach(featureFlag =>
                {
                    const existing = this.featureFlags.get(featureFlag.Flag);
                    if (existing)
                        existing.set(featureFlag.Enabled ?? false);
                    else
                    {
                        const flag = signal<boolean>(featureFlag.Enabled ?? false);
                        this.featureFlags.set(featureFlag.Flag, flag);
                    }
                });
            })
            .finally(() =>
            {
                this.initPromise = undefined;
            });
        this.ready = this.initPromise;
    }

    private get forceAllFeatureFlags(): Signal<boolean>
    {
        let flag = this.featureFlags.get(FeatureFlags.ResolveAllFeatureFlagsTo);
        if (!flag)
        {
            flag = signal<boolean>(false);
            this.featureFlags.set(FeatureFlags.ResolveAllFeatureFlagsTo, flag);
        }

        return flag;
    }

    get DevFlag(): Signal<boolean>
    {
        return this.get(FeatureFlags.All_DevFlag);
    }

    get isDevFlagOn(): boolean
    {
        return this.get(FeatureFlags.All_DevFlag)();
    }

    get whenReady(): Promise<void>
    {
        return this.ready;
    }

    @LogInvocation(LogLevel.DEBUG)
    get(flagName: string): Signal<boolean>
    {
        let flag = this.featureFlags.get(flagName);
        if (!flag)
        {
            flag = signal<boolean>(false);
            this.featureFlags.set(flagName, flag);
        }
        // TODO - this could/would be a little jarring.... we might need some sort of interval refresh, or a method to update all flags from the backend.
        // this.GetFlag(flagName)
        //     .then((featureFlag: FeatureFlagViewModel) =>
        //     {
        //         flag.set(featureFlag.Enabled ?? false);
        //     });

        return computed(() =>
        {
            return this.forceAllFeatureFlags() || flag();
        });
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private GetFlags(): Promise<Array<FeatureFlagViewModel>>
    {
        return this.$apiService.sendUnauth("IFeatureFlagService/GetFlags");
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private GetFlag(flagName: string): Promise<FeatureFlagViewModel>
    {
        return this.$apiService.sendUnauth("IFeatureFlagService/GetFlags", flagName);
    }

    static get isDevFlagOn(): boolean
    {
        return FeatureFlagHelperService.instance?.isDevFlagOn ?? false;
    }
}
